import {Component, inject, OnInit, signal} from '@angular/core';
import {BaseComponent} from "../../../shared/base/base-component";
import {
  ColumnDef,
  DataTableComponent,
  TableActionEvent,
  TableStateEvent
} from "../../../shared/components/data-table/data-table.component";

import {State} from "../../../shared/base/base-state";
import {ApiService} from "../../../core/api/api.service";
import {environment} from "../../../../environments/environment";
import {CreateNewCompanyComponent} from "./create-new-company/create-new-company.component";
import {OverlayService} from "../../../shared/components/overlay/overlay.service";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {DropdownComponent} from "../../../shared/inputs/dropdown/dropdown.component";
import {
  GetCompanyDelegationTokenParams,
  GetCompanyDelegationTokenResponse,
  GetCompanyListItem,
  GetCompanyListParams,
  GetCompanyListResponse,
  GetCompanyListStatus
} from "../../../core/api/model";
import {ButtonComponent} from "../../../shared/components/button/button.component";
import {Constants} from "../../../core/constants/constants";
import {UserRole} from "../../../core/services/token/token-store.service";


@Component({
  selector: 'app-company-list',
  standalone: true,
  imports: [
    DataTableComponent,
    FormsModule,
    DropdownComponent,
    ReactiveFormsModule,
    ButtonComponent,
  ],
  templateUrl: './company-list.component.html',
  styleUrl: './company-list.component.scss'
})
export class CompanyListComponent extends BaseComponent implements OnInit {

  apiService = inject(ApiService);
  overlayService = inject(OverlayService);


  companyListState = new State<GetCompanyListResponse>();
  tableState = signal<TableStateEvent | null>(null);
  private companyDelegationTokenState = new State<GetCompanyDelegationTokenResponse>();

  // ✅ Correct type: dropdown item or null
  statusFilter: { label: string; value: GetCompanyListStatus | null } | null = null;

  statuses: { label: string; value: GetCompanyListStatus | null }[] = [];

  columnDefs: ColumnDef[] = [
    {title: 'Code', type: 'text', displayTemplate: '$code', alignment: 'center'},
    {title: 'Company Name', type: 'text', displayTemplate: '$companyName', alignment: 'left'},
    {title: 'DOT Number', type: 'text', displayTemplate: '$dotNumber', alignment: 'left'},
    {title: 'Contact Name', type: 'text', displayTemplate: '$firstName $lastName', alignment: 'left'},
    {title: 'City', type: 'text', displayTemplate: '$city, $stateName', alignment: 'left'},
    {
      title: 'Status',
      type: 'badge',
      displayTemplate: '$companyStatus',
      alignment: 'center',
      badgeConfig: {
        properties: [
          {
            data: 'Active',
            displayText: 'Active',
            backgroundColorClass: 'bg-success-50',
            borderColorClass: 'border-transparent',
            textColorClass: 'text-success-500'
          },
          {
            data: 'Inactive',
            displayText: 'Inactive',
            backgroundColorClass: 'bg-error-50',
            borderColorClass: 'border-transparent',
            textColorClass: 'text-error-500'
          },
        ]
      },
    },
    {
      title: 'Actions',
      type: 'actions',
      alignment: 'center',
      actionsConfig: {
        iconActions: [
          {
            iconPath: 'assets/icons/login.svg',
            actionKey: 'login',
            label: 'Login'
          },
          {
            iconPath: 'assets/icons/eye.svg',
            actionKey: 'view',
            label: 'View'
          }
        ]
      }
    }
  ];

  ngOnInit() {
    this.userRole = this.tokenService.getUserRole();
    this.populateDropDownStatus();
  }

  getCompanyList(event: TableStateEvent) {
    const request: GetCompanyListParams = {
      pageNumber: event.paginationEvent?.pageNumber,
      pageSize: event.paginationEvent?.pageSize,
      status: this.statusFilter?.value ?? undefined, // ✅ uses selected filter
      search: event.searchText,
    };
    this.executeRequest<GetCompanyListResponse>({
      state: this.companyListState,
      request: this.apiService.getCompanyList(request),
    })
  }

  onActionClicked($event: TableActionEvent) {
    const item: GetCompanyListItem = $event.item;
    switch ($event.actionKey) {
      case 'login':
        this.loginAsCompany(item.id);
        break;
      case 'view':
        this.router.navigate([`/companies/${item.id}`]).then();
        break;
    }
  }

  onDropDownFilterSelected() {
    if (this.tableState()) {
      this.getCompanyList(this.tableState()!);
    }
  }

  populateDropDownStatus() {
    const statusList: { label: string; value: GetCompanyListStatus | null }[] =
      Object.values(GetCompanyListStatus).map((status) => ({
        label: status,
        value: status as GetCompanyListStatus
      }));

    statusList.unshift({
      label: 'All',
      value: null
    });

    this.statuses = statusList;
    this.statusFilter = this.statuses.at(0) ?? null; // ✅ default "All"
  }

  private loginAsCompany(id: string) {
    const request: GetCompanyDelegationTokenParams = {companyId: id};
    this.executeRequest<GetCompanyDelegationTokenResponse>({
      state: this.companyDelegationTokenState,
      request: this.apiService.getCompanyDelegationToken(request),
      onSuccess: response => {
        window.open(`${environment.companyPortalUrl}/company-login?token=${response.data?.token}`, '_blank');
      }
    });
  }

  onCreateNewCompanyClicked() {
    this.overlayService.openModal(CreateNewCompanyComponent).closed.subscribe((result) => {
      if (result) {
        this.getCompanyList(Constants.defaultTableStateEvent)
      }
    });
  }

  protected readonly UserRole = UserRole;
}
